import { useNavigate } from 'react-router-dom';

import {
  ContentNugget,
  PanelNugget,
  mapCareerPathResponseToCareerPath,
} from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/App';
import { nuggetMapper } from '@backrs_ui/grow-ui/src/components/Nuggets/nugget.util';
import {
  LubavResource,
  LubavCareerPath,
  LubavCollege,
  LubavCompetency,
  LubavContact,
  LubavInternship,
  LubavScholarship,
  ResourceKey,
  RESOURCE_KEY,
} from '@backrs_ui/grow-ui/src/models/models';
import { collegeTransform } from '@backrs_ui/grow-ui/src/pages/Search/search.nuggetTransformers';
import { setCurrentDialogNugget } from '@backrs_ui/grow-ui/src/redux/contentNugget/contentNugget';
import { useAppDispatch } from '@backrs_ui/grow-ui/src/redux/hooks';

// these nuggets need tranform since they are not from content_nugget
const nuggetsToMap: ResourceKey[] = [
  RESOURCE_KEY.COLLEGES,
  RESOURCE_KEY.COMPETENCIES,
  RESOURCE_KEY.INTERNSHIPS,
  RESOURCE_KEY.SCHOLARSHIPS,
  RESOURCE_KEY.CAREER_PATHS,
  RESOURCE_KEY.BACKRS,
  RESOURCE_KEY.STUDENTS,
  RESOURCE_KEY.PROVIDERS,
];

const nuggetsWithBody: ResourceKey[] = [
  RESOURCE_KEY.COMPETENCIES,
  RESOURCE_KEY.INTERNSHIPS,
  RESOURCE_KEY.SCHOLARSHIPS,
];

type LubavResourceTypes =
  | LubavCareerPath
  | LubavCollege
  | LubavCompetency
  | LubavContact
  | LubavInternship
  | LubavScholarship
  | LubavResource;

type ResourceNuggetProps = Readonly<{
  type: ResourceKey;
  resource: LubavResourceTypes;
  disableExpansion?: boolean;
}>;

type MapResourceToContentNuggetProps = Readonly<{
  type: ResourceKey;
  resource: LubavResourceTypes;
}>;

const mapResourceToContentNugget = ({
  type,
  resource,
}: MapResourceToContentNuggetProps): ContentNugget | null => {
  const baseNugget = {
    id: Number('id' in resource ? resource.id : resource.metadata?.id) || 0,
    star_rating: 0,
    value_rating: 0,
    is_browsable: true,
  };

  switch (type) {
    case RESOURCE_KEY.CAREER_PATHS: {
      const careerPath = resource as LubavCareerPath;

      return {
        ...baseNugget,
        slug: careerPath.metadata.name.toLowerCase().replace(/\s+/g, '-'),
        title: careerPath.metadata.name,
        description: careerPath.metadata.description,
        variant: 'career-path',
        career_path_info: mapCareerPathResponseToCareerPath({
          ...careerPath.metadata,
        }),
      };
    }

    case RESOURCE_KEY.COLLEGES: {
      const college = resource as LubavCollege;

      if (!college.id) {
        return null;
      }

      return collegeTransform(college.metadata);
    }

    case RESOURCE_KEY.COMPETENCIES: {
      const competency = resource as LubavCompetency;

      return {
        ...baseNugget,
        slug: competency.metadata.slug,
        title: competency.metadata.title,
        description: competency.metadata.description,
        variant: 'competency',
      };
    }

    case RESOURCE_KEY.INTERNSHIPS: {
      const internship = resource as LubavInternship;

      return {
        ...baseNugget,
        title: internship.metadata.role_title,
        description: internship.metadata.ai_description,
        slug: internship.metadata.id.toString(),
        variant: 'internship',
        is_browsable: false,
        internship_info: internship.metadata,
        content_nugget_theme: 'Internship',
      };
    }

    case RESOURCE_KEY.BACKRS:
    case RESOURCE_KEY.STUDENTS:
    case RESOURCE_KEY.PROVIDERS: {
      const { id, relevance, metadata } = resource as LubavContact;
      const { name } = metadata;

      return {
        id: Number(id),
        slug: name.toLowerCase().replace(/\s+/g, '-'),
        title: name,
        description: relevance || '',
        variant: 'profile-internal',
        is_browsable: true,
        star_rating: 0,
        value_rating: 0,
        profile_info: {
          id: Number(id),
          first_name: name.split(' ')[0] || '',
          last_name: name.split(' ').slice(1).join(' ') || '',
          profile_image: metadata.profile_image || undefined,
          profile_cover_image: metadata.profile_cover_image || undefined,
          email: '',
          access_token: '',
          school: metadata.school || undefined,
          job_title: metadata.job_title || undefined,
          employer: metadata.employer || undefined,
        },
      };
    }

    case RESOURCE_KEY.SCHOLARSHIPS: {
      const scholarship = resource as LubavScholarship;

      return {
        ...baseNugget,
        slug: (scholarship.metadata?.name || '').toLowerCase().replace(/\s+/g, '-'),
        title: scholarship.metadata?.name || '',
        description: scholarship.metadata?.description || '',
        variant: 'scholarships',
        scholarship_info: scholarship.metadata,
      };
    }

    default: {
      return (resource as LubavResource).metadata || null;
    }
  }
};

export const ResourceNugget = ({
  type,
  resource,
  disableExpansion = false,
}: ResourceNuggetProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const handlePanelNuggetClick = (nugget: ContentNugget) => {
    switch (nugget.variant) {
      case 'competency':
        navigate(`${APP_ROUTE.COMPETENCY}/${nugget.slug}`);
        break;
      case 'institution':
        navigate(`${APP_ROUTE.UNIVERSITY}/${nugget.id}`);
        break;
      default:
        dispatch(setCurrentDialogNugget(nugget));
        break;
    }
  };

  const resourceNugget = nuggetsToMap.includes(type)
    ? mapResourceToContentNugget({ type, resource })
    : (resource as LubavResource).metadata || null;

  if (!resourceNugget) {
    return null;
  }

  return (
    <PanelNugget
      key={resourceNugget.id}
      title={resourceNugget.title}
      body={resourceNugget.description}
      size="SMALL"
      variant={nuggetMapper({ nugget: resourceNugget })}
      subText={resource.relevance}
      hideBody={!nuggetsWithBody.includes(type)}
      fitContent={true}
      onClick={
        disableExpansion
          ? undefined
          : () => {
              handlePanelNuggetClick(resourceNugget);
            }
      }
    />
  );
};
